import { useMemo, useState } from "react";
import { AddCircle, Close, DeleteOutline, Edit, MoreVert } from "@mui/icons-material";
import { Apartment, Checklist, Description } from "@mui/icons-material";
import { toast } from "sonner";
import { DocumentationAgency, DocumentationStep, DocumentationTask, ResourceSchema } from "../../data/types";
import { useResourceHook } from "../../hooks/resourcehooks";
import {
  collectAgencies,
  createEmptyStep,
  createEmptyTask,
  ensureStepsHaveDefaultTasks,
  getArlingtonStarterSteps,
  hasAgencyContact,
} from "../../utils/documentationSteps";
import DocumentationAgencyFields from "./DocumentationAgencyFields";

type DocumentationStepsPanelProps = {
  resource: ResourceSchema
  resourceId: string
  resourceType: string
  onOpenEditor?: () => void
};

type StepDraft = DocumentationStep;

const ModalShell = ({
  isOpen,
  onClose,
  children,
  testId,
  widthClass = "max-w-xl",
}: {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  testId: string
  widthClass?: string
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-6">
      <div className="fixed inset-0 z-40 bg-white/90" onClick={onClose} />
      <dialog
        open
        data-testid={testId}
        className={`relative z-50 w-full ${widthClass} max-h-[95vh] overflow-hidden rounded-xl bg-white shadow-[0_0px_8px_rgba(0,0,0,0.16)]`}
        style={{ margin: 0 }}
      >
        {children}
      </dialog>
    </div>
  );
};

/** Steps tab: add, view, edit, and delete documentation steps and nested tasks. */
const DocumentationStepsPanel = ({ resource, resourceId, resourceType }: DocumentationStepsPanelProps) => {
  const steps = useMemo(() => resource.steps || [], [resource.steps]);
  const { updateDocumentationSteps } = useResourceHook({ resourceId, resourceType: resourceType as any });
  const agencies = useMemo(() => collectAgencies(steps), [steps]);

  const [isEditStepsOpen, setIsEditStepsOpen] = useState(false);
  const [editorTab, setEditorTab] = useState<"details" | "tasks">("details");
  const [draftSteps, setDraftSteps] = useState<StepDraft[]>([]);
  const [editingStep, setEditingStep] = useState<StepDraft | null>(null);
  const [deletingStepId, setDeletingStepId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const openAddSteps = () => {
    setDraftSteps(ensureStepsHaveDefaultTasks([createEmptyStep(0)]));
    setEditorTab("details");
    setIsEditStepsOpen(true);
  };

  const openEditSteps = (initial?: DocumentationStep[]) => {
    setDraftSteps(ensureStepsHaveDefaultTasks(initial && initial.length > 0 ? initial : [createEmptyStep(0)]));
    setEditorTab("details");
    setIsEditStepsOpen(true);
  };

  const seedArlington = () => {
    setDraftSteps(getArlingtonStarterSteps());
    setEditorTab("details");
    setIsEditStepsOpen(true);
  };

  const persistSteps = async (nextSteps: DocumentationStep[]) => {
    setIsSaving(true);
    const success = await updateDocumentationSteps(ensureStepsHaveDefaultTasks(nextSteps));
    setIsSaving(false);
    if (success) {
      toast.success("Steps saved successfully!");
      setIsEditStepsOpen(false);
      setEditingStep(null);
      setDeletingStepId(null);
      return true;
    }
    toast.error("Error saving steps. Please try again.");
    return false;
  };

  const handleConfirmSingleStep = async () => {
    if (!editingStep) return;
    const exists = steps.some((step) => step.id === editingStep.id);
    const next = exists
      ? steps.map((step) => (step.id === editingStep.id ? editingStep : step))
      : [...steps, { ...editingStep, order: steps.length }];
    await persistSteps(next);
  };

  const handleDeleteStep = async () => {
    if (!deletingStepId) return;
    await persistSteps(steps.filter((step) => step.id !== deletingStepId));
  };

  const updateDraftStep = (stepId: string, updates: Partial<DocumentationStep>) => {
    setDraftSteps((prev) =>
      prev.map((step) => (step.id === stepId ? { ...step, ...updates } : step))
    );
  };

  const updateDraftTask = (stepId: string, taskId: string, updates: Partial<DocumentationTask>) => {
    setDraftSteps((prev) =>
      prev.map((step) => {
        if (step.id !== stepId) return step;
        return {
          ...step,
          tasks: (step.tasks || []).map((task) => (task.id === taskId ? { ...task, ...updates } : task)),
        };
      })
    );
  };

  return (
    <div className="mx-auto w-11/12 py-6">
      {steps.length === 0 ? (
        <div className="mx-auto my-12 flex h-[350px] w-[423px] rounded-lg bg-white">
          <div className="m-auto flex w-10/12 flex-col">
            <div className="mx-auto mt-auto flex h-[80px] w-[80px] rounded-full bg-[#EFF8FA]">
              <Checklist className="m-auto text-primary-400" style={{ height: 40, width: 40 }} />
            </div>
            <p className="mt-4 text-center font-raleway text-[20px] font-semibold text-primary-200">No Steps Yet</p>
            <p className="my-3 text-center font-open-sans text-[16px] text-[#333333]">
              Add steps and applicant tasks for this documentation resource.
            </p>
            <button
              className="my-2 w-full rounded-md bg-primary-200 p-2 font-raleway text-[16px] font-medium text-white"
              onClick={openAddSteps}
              data-testid="add-steps-empty"
            >
              <AddCircle className="my-auto mx-2 text-white" style={{ height: 24, width: 24 }} />
              Add Steps
            </button>
            <button
              className="mb-2 w-full rounded-md border border-[#E9E9E9] p-2 font-raleway text-[14px] font-medium text-primary-200"
              onClick={seedArlington}
              data-testid="use-arlington-starter"
            >
              Use Arlington starter checklist
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-row">
            <button
              className="ml-auto rounded-md bg-primary-200 px-3 py-2 font-raleway text-[16px] font-medium text-white"
              onClick={() => openEditSteps(steps)}
              data-testid="edit-steps-button"
            >
              <Edit className="my-auto mr-1" style={{ height: 18, width: 18 }} />
              Edit Steps
            </button>
          </div>
          <div className="flex flex-col gap-4">
            {steps.map((step, index) => (
              <div key={step.id} className="relative rounded-xl border border-[#E9E9E9] bg-white p-5" data-testid={`step-card-${step.id}`}>
                <div className="mb-2 flex flex-row items-start">
                  <span className="rounded-full bg-[#F3F4F6] px-3 py-1 font-raleway text-[12px] text-[#4D4D4D]">
                    Step {index + 1}
                  </span>
                  <button
                    className="ml-auto text-[#808080]"
                    aria-label={`Step ${index + 1} actions`}
                    onClick={() => setOpenMenuId(openMenuId === step.id ? null : step.id)}
                  >
                    <MoreVert />
                  </button>
                </div>
                {openMenuId === step.id ? (
                  <div className="absolute right-5 top-12 z-10 w-40 rounded-lg border border-[#E9E9E9] bg-white py-1 shadow-md">
                    <button
                      className="flex w-full px-3 py-2 text-left font-raleway text-[14px] hover:bg-[#F8FAFB]"
                      onClick={() => {
                        setEditingStep(ensureStepsHaveDefaultTasks([step])[0]);
                        setOpenMenuId(null);
                      }}
                    >
                      Edit Step
                    </button>
                    <button
                      className="flex w-full px-3 py-2 text-left font-raleway text-[14px] text-[#D42620] hover:bg-[#F8FAFB]"
                      onClick={() => {
                        setDeletingStepId(step.id);
                        setOpenMenuId(null);
                      }}
                    >
                      Delete Step
                    </button>
                  </div>
                ) : null}
                <h3 className="font-raleway text-[18px] font-semibold text-black">{step.title || "Untitled step"}</h3>
                <p className="mt-1 font-open-sans text-[14px] text-[#4D4D4D]">{step.description || "No description yet."}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {hasAgencyContact(step.agency) ? (
                    <span className="inline-flex items-center rounded-full bg-[#EFF8FA] px-3 py-1 font-raleway text-[12px] text-primary-200">
                      <Apartment className="mr-1" style={{ height: 14, width: 14 }} />
                      {step.agency?.name}
                    </span>
                  ) : null}
                  <span className="rounded-full bg-[#F3F4F6] px-3 py-1 font-raleway text-[12px] text-[#4D4D4D]">
                    {(step.tasks || []).length} task{(step.tasks || []).length === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
            ))}
            <button
              className="flex items-center font-raleway text-[15px] font-medium text-primary-200"
              onClick={() => persistSteps([...steps, createEmptyStep(steps.length)])}
            >
              <AddCircle className="mr-2" style={{ height: 20, width: 20 }} />
              Add Step
            </button>
          </div>
        </>
      )}

      <ModalShell isOpen={Boolean(editingStep)} onClose={() => setEditingStep(null)} testId="edit-step-modal">
        <div className="overflow-y-auto p-6">
          <h2 className="font-raleway text-[22px] font-bold text-black">Edit Step</h2>
          <p className="mb-4 font-open-sans text-[14px] text-[#808080]">View and edit the details of this step</p>
          <label className="mb-3 block">
            <span className="mb-1 block font-raleway text-[14px] text-black">Title</span>
            <input
              className="w-full rounded-lg border border-[#E9E9E9] px-3 py-2.5 font-open-sans text-[14px]"
              value={editingStep?.title || ""}
              onChange={(event) => setEditingStep((prev) => prev ? { ...prev, title: event.target.value } : prev)}
            />
          </label>
          <label className="mb-3 block">
            <span className="mb-1 block font-raleway text-[14px] text-black">Agency</span>
            <DocumentationAgencyFields
              agency={editingStep?.agency}
              agencies={agencies}
              showContactFields={false}
              onChange={(agency: DocumentationAgency) => setEditingStep((prev) => prev ? { ...prev, agency } : prev)}
            />
          </label>
          <label className="mb-6 block">
            <span className="mb-1 block font-raleway text-[14px] text-black">Description</span>
            <textarea
              className="min-h-[110px] w-full rounded-lg border border-[#E9E9E9] px-3 py-2.5 font-open-sans text-[14px]"
              value={editingStep?.description || ""}
              onChange={(event) => setEditingStep((prev) => prev ? { ...prev, description: event.target.value } : prev)}
            />
          </label>
          <div className="flex justify-end gap-2">
            <button className="rounded-md border border-[#E9E9E9] px-4 py-2 font-raleway text-primary-200" onClick={() => setEditingStep(null)}>
              Cancel
            </button>
            <button
              className="rounded-md bg-primary-200 px-4 py-2 font-raleway text-white disabled:opacity-60"
              disabled={isSaving}
              onClick={handleConfirmSingleStep}
            >
              Confirm Changes
            </button>
          </div>
        </div>
      </ModalShell>

      <ModalShell isOpen={Boolean(deletingStepId)} onClose={() => setDeletingStepId(null)} testId="delete-step-modal">
        <div className="p-6">
          <h2 className="font-raleway text-[22px] font-bold text-black">Delete Step?</h2>
          <p className="my-3 font-open-sans text-[15px] text-[#333333]">Are you sure you want to delete this step?</p>
          <div className="mt-4 flex justify-end gap-2">
            <button className="rounded-md border border-[#E9E9E9] px-4 py-2 font-raleway text-primary-200" onClick={() => setDeletingStepId(null)}>
              No, Cancel
            </button>
            <button className="rounded-md bg-[#D42620] px-4 py-2 font-raleway text-white" onClick={handleDeleteStep}>
              Yes, Delete
            </button>
          </div>
        </div>
      </ModalShell>

      <ModalShell isOpen={isEditStepsOpen} onClose={() => setIsEditStepsOpen(false)} testId="edit-steps-modal" widthClass="max-w-2xl">
        <div className="flex max-h-[95vh] flex-col">
          <div className="flex items-start p-6 pb-3">
            <div>
              <h2 className="font-raleway text-[22px] font-bold text-black">Edit Steps</h2>
              <p className="font-open-sans text-[14px] text-[#808080]">Update this documentation checklist.</p>
            </div>
            <button className="ml-auto text-[#A1A1A1]" aria-label="Close" onClick={() => setIsEditStepsOpen(false)}>
              <Close />
            </button>
          </div>
          <div className="mx-6 mb-3 flex gap-2">
            <button
              className={`flex items-center rounded-full px-4 py-2 font-raleway text-[13px] ${editorTab === "details" ? "bg-primary-400 text-white" : "bg-[#F3F4F6] text-[#4D4D4D]"}`}
              onClick={() => setEditorTab("details")}
            >
              <Description className="mr-1" style={{ height: 16, width: 16 }} />
              Steps Details
            </button>
            <button
              className={`flex items-center rounded-full px-4 py-2 font-raleway text-[13px] ${editorTab === "tasks" ? "bg-primary-400 text-white" : "bg-[#F3F4F6] text-[#4D4D4D]"}`}
              onClick={() => setEditorTab("tasks")}
            >
              <Checklist className="mr-1" style={{ height: 16, width: 16 }} />
              Checklist Tasks
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-6 pb-4">
            {editorTab === "details" ? (
              <div className="flex flex-col gap-4">
                {draftSteps.map((step, index) => (
                  <div key={step.id} className="rounded-xl border border-[#E9E9E9] p-4">
                    <div className="mb-3 flex items-center">
                      <span className="rounded-full bg-[#F3F4F6] px-3 py-1 font-raleway text-[12px]">Step {index + 1}</span>
                      <button className="ml-auto text-[#D42620]" aria-label={`Delete step ${index + 1}`} onClick={() => setDraftSteps((prev) => prev.filter((item) => item.id !== step.id))}>
                        <DeleteOutline />
                      </button>
                    </div>
                    <input
                      className="mb-2 w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-raleway text-[15px] font-semibold"
                      placeholder="Step title"
                      value={step.title || ""}
                      onChange={(event) => updateDraftStep(step.id, { title: event.target.value })}
                    />
                    <textarea
                      className="mb-2 min-h-[80px] w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-[14px]"
                      placeholder="Describe this checkpoint..."
                      value={step.description || ""}
                      onChange={(event) => updateDraftStep(step.id, { description: event.target.value })}
                    />
                    <DocumentationAgencyFields
                      agency={step.agency}
                      agencies={agencies}
                      showContactFields={false}
                      onChange={(agency) => updateDraftStep(step.id, { agency })}
                    />
                  </div>
                ))}
                <button className="flex items-center font-raleway text-primary-200" onClick={() => setDraftSteps((prev) => [...prev, createEmptyStep(prev.length)])}>
                  <AddCircle className="mr-2" style={{ height: 18, width: 18 }} />
                  Add Step
                </button>
                {steps.length === 0 ? (
                  <button className="text-left font-raleway text-[13px] text-primary-200 underline" onClick={() => setDraftSteps(getArlingtonStarterSteps())}>
                    Use Arlington starter checklist
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {draftSteps.map((step, stepIndex) => (
                  <div key={step.id}>
                    <p className="mb-2 font-raleway text-[13px] font-semibold text-[#808080]">
                      {step.title || `Step ${stepIndex + 1}`}
                    </p>
                    {(step.tasks || []).map((task, taskIndex) => (
                      <div key={task.id} className="mb-3 rounded-xl border border-[#E9E9E9] p-4">
                        <div className="mb-3 flex items-center">
                          <span className="rounded-full bg-[#F3F4F6] px-3 py-1 font-raleway text-[12px]">Task {taskIndex + 1}</span>
                          <button
                            className="ml-auto text-[#D42620]"
                            aria-label={`Delete task ${taskIndex + 1}`}
                            onClick={() =>
                              updateDraftStep(step.id, {
                                tasks: (step.tasks || []).filter((item) => item.id !== task.id),
                              })
                            }
                          >
                            <DeleteOutline />
                          </button>
                        </div>
                        <input
                          className="mb-2 w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-raleway text-[15px] font-semibold"
                          placeholder="Task Title"
                          value={task.title || ""}
                          onChange={(event) => updateDraftTask(step.id, task.id, { title: event.target.value })}
                        />
                        <textarea
                          className="mb-2 min-h-[80px] w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-[14px]"
                          placeholder="Describe what the applicant needs to do..."
                          value={task.description || ""}
                          onChange={(event) => updateDraftTask(step.id, task.id, { description: event.target.value })}
                        />
                        <DocumentationAgencyFields
                          agency={task.agency}
                          agencies={agencies}
                          onChange={(agency) => updateDraftTask(step.id, task.id, { agency })}
                        />
                      </div>
                    ))}
                    <button
                      className="mb-2 flex items-center font-raleway text-[14px] text-primary-200"
                      onClick={() =>
                        updateDraftStep(step.id, {
                          tasks: [...(step.tasks || []), createEmptyTask((step.tasks || []).length)],
                        })
                      }
                    >
                      <AddCircle className="mr-2" style={{ height: 18, width: 18 }} />
                      Add Task
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="p-6 pt-2">
            <button
              className="w-full rounded-md bg-primary-200 py-3 font-raleway text-[16px] font-medium text-white disabled:opacity-60"
              disabled={isSaving || draftSteps.length === 0}
              onClick={() => persistSteps(draftSteps)}
              data-testid="save-tasks-button"
            >
              Save Tasks
            </button>
          </div>
        </div>
      </ModalShell>
    </div>
  );
};

export default DocumentationStepsPanel;
